import { useState } from 'react'
import { Clock, AlertCircle } from 'lucide-react'
import TestRunner from '../components/TestRunner/TestRunner'

export default function TestMode({ state, updateState }) {
  const [testConfig, setTestConfig] = useState(null)
  const [showConfig, setShowConfig] = useState(true)

  const [config, setConfig] = useState({
    testType: 'stage1',
    modules: ['oir'],
    duration: 30,
    questionCount: 10,
  })

  const testTypes = [
    { id: 'stage1', name: 'Stage 1 (Screening)', modules: ['oir'] },
    { id: 'stage2', name: 'Stage 2 (Psychology)', modules: ['wat', 'tat', 'srt'] },
    { id: 'full', name: 'Full Mock Test', modules: ['oir', 'wat', 'tat', 'srt'] },
  ]

  const handleStartTest = () => {
    if (config.modules.length === 0) {
      alert('Please select at least one module')
      return
    }

    setTestConfig({
      ...config,
      startTime: new Date().toISOString(),
    })
    setShowConfig(false)
  }

  const handleTestComplete = (results) => {
    // Save test results
    updateState('progress.tests.history', [
      ...(state.progress.tests?.history || []),
      {
        ...results,
        config: testConfig,
        completedAt: new Date().toISOString(),
      },
    ])

    // Reset for next test
    setTestConfig(null)
    setShowConfig(true)
  }

  if (!showConfig && testConfig) {
    return (
      <TestRunner
        config={testConfig}
        onComplete={handleTestComplete}
        onCancel={() => {
          setTestConfig(null)
          setShowConfig(true)
        }}
        state={state}
        updateState={updateState}
      />
    )
  }

  return (
    <div className="space-y-6 animate-slide-in max-w-4xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold mb-2">Test Mode</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Configure and take a timed test with randomized questions
        </p>
      </div>

      <div className="card bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-yellow-800 dark:text-yellow-200">Test Mode Rules</h3>
            <ul className="text-sm text-yellow-700 dark:text-yellow-300 mt-2 space-y-1">
              <li>• Questions are randomized and timed</li>
              <li>• You cannot go back once submitted</li>
              <li>• Results are shown only after completion</li>
              <li>• Your test seed is recorded for reproducibility</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-bold mb-4">Test Configuration</h2>

        {/* Test Type */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2">Test Type</label>
          <div className="space-y-2">
            {testTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => {
                  setConfig({ ...config, testType: type.id, modules: type.modules })
                }}
                className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                  config.testType === type.id
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'
                }`}
              >
                <div className="font-semibold">{type.name}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Modules: {type.modules.join(', ').toUpperCase()}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Duration */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2">
            Duration (minutes)
          </label>
          <input
            type="number"
            min="5"
            max="120"
            value={config.duration}
            onChange={(e) => setConfig({ ...config, duration: parseInt(e.target.value) || 30 })}
            className="input-field"
          />
        </div>

        {/* Question Count */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2">
            Number of Questions (per module)
          </label>
          <input
            type="number"
            min="5"
            max="50"
            value={config.questionCount}
            onChange={(e) =>
              setConfig({ ...config, questionCount: parseInt(e.target.value) || 10 })
            }
            className="input-field"
          />
        </div>

        <button onClick={handleStartTest} className="btn-primary w-full">
          <Clock className="w-5 h-5 inline mr-2" />
          Start Test
        </button>
      </div>

      {/* Test History */}
      {state.progress.tests?.history && state.progress.tests.history.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Recent Tests</h2>
          <div className="space-y-3">
            {state.progress.tests.history.slice(-5).reverse().map((test, index) => (
              <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="font-semibold">
                      {test.config?.testType?.toUpperCase() || 'Test'}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      {new Date(test.completedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <span className="text-lg font-bold text-primary-600">
                    {test.totalScore || 0}%
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Duration: {test.config?.duration}min | Questions: {test.totalQuestions || 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
